import { AiChatbotConversation } from '@va/dashboard/shared/ai-assistant';
import { FallbackIconCircle, OpenAiIcon } from '@va/icons';
import { useLocalizationContext } from '@va/localization';
import { getLocalizedDate } from '@va/util/helpers';
import dayjs, { Dayjs } from 'dayjs';
import { AiModelProviderNames } from './constants';

export const getAiModelIcon = (modelName: string) => {
  const [provider] = modelName.split('/');

  switch (provider) {
    case AiModelProviderNames.openai:
      return <OpenAiIcon />;
    default:
      return <FallbackIconCircle className='w-10u h-10u' />;
  }
};

const chatIntervalCategories = [
  {
    key: 'today',
    label: 'aiChatbot.chatsHistory.intervalCategories.today',
    compareFn: (date: Dayjs) => date.isSame(dayjs(), 'day'),
  },
  {
    key: 'yesterday',
    label: 'aiChatbot.chatsHistory.intervalCategories.yesterday',
    compareFn: (date: Dayjs) => date.isSame(dayjs().subtract(1, 'day'), 'day'),
  },
  {
    key: 'last7Days',
    label: 'aiChatbot.chatsHistory.intervalCategories.last7Days',
    compareFn: (date: Dayjs) => date.isAfter(dayjs().subtract(7, 'day')),
  },
  {
    key: 'older',
    label: 'aiChatbot.chatsHistory.intervalCategories.older',
    compareFn: (date: Dayjs) => !date.isAfter(dayjs().subtract(7, 'day')),
  },
];

export const useGroupedChatsByInterval = (chats: AiChatbotConversation[]) => {
  const { translate, locale } = useLocalizationContext();

  const chatsCopy = [...chats];
  const categories: { [key: string]: { key: string; label: string; chats: AiChatbotConversation[] } } = {};
  let minDate: Dayjs | null = null;
  let maxDate: Dayjs | null = null;

  while (chatsCopy.length > 0) {
    const chat = chatsCopy.shift();
    if (!chat) break;

    const chatDate = dayjs(chat.createdAt);
    const category = chatIntervalCategories.find((category) => category.compareFn(chatDate));

    if (minDate === null || maxDate === null) {
      minDate = chatDate;
      maxDate = chatDate;
    }

    if (chatDate.isBefore(minDate)) {
      minDate = chatDate;
    }

    if (chatDate.isAfter(maxDate)) {
      maxDate = chatDate;
    }

    if (category) {
      const existingCategory = categories[category.key];
      if (existingCategory) {
        existingCategory.chats.push(chat);
      } else {
        let label: string = translate(category.label);

        if (category.key === 'older' && minDate && maxDate) {
          label = `${getLocalizedDate(minDate.unix(), locale)} - ${getLocalizedDate(maxDate.unix(), locale)}`;
        }

        categories[category.key] = { key: category.key, label, chats: [chat] };
      }
    }
  }

  return Object.values(categories);
};
